import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Users, Building2, TrendingUp, AlertTriangle, Layers, GitBranch } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend,
} from "recharts";

const COLORS = ["#002FA7", "#5B5FC7", "#0A7029", "#D83B01", "#C50F1F", "#8764B8", "#2F2F2F", "#7B7B7B"];

export default function Analytics() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/analytics/headcount").then((r) => setData(r.data)).catch((e) => setErr(e.message));
  }, []);

  if (err) {
    return <div className="p-12 text-sm text-[#C50F1F]">Could not load analytics.</div>;
  }
  if (!data) {
    return <div className="p-12 text-xs uppercase tracking-[0.2em] text-neutral-500">Loading…</div>;
  }

  return (
    <div className="min-h-screen p-8 md:p-12" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
      <div className="mb-10">
        <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-500" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
          Org Intelligence
        </p>
        <h1 className="mt-2 text-4xl tracking-tight" style={{ fontFamily: '"Work Sans", sans-serif' }}>
          Analytics
        </h1>
      </div>

      {/* KPI grid */}
      <div className="mb-10 grid grid-cols-2 gap-px border border-neutral-200 bg-neutral-200 md:grid-cols-4 lg:grid-cols-6">
        <KPI icon={Users} label="Total" value={data.total} testId="kpi-total"/>
        <KPI icon={GitBranch} label="Managers" value={data.managers} testId="kpi-managers"/>
        <KPI icon={Users} label="Individual Contributors" value={data.individual_contributors} testId="kpi-ics"/>
        <KPI icon={Building2} label="Departments" value={data.departments} testId="kpi-depts"/>
        <KPI icon={TrendingUp} label="Avg span of control" value={data.average_span_of_control} testId="kpi-span"/>
        <KPI icon={Layers} label="Org depth" value={data.max_depth} testId="kpi-depth"/>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* By department */}
        <Panel title="Headcount by department">
          <ResponsiveContainer width="100%" height={Math.max(220, data.by_department.length * 28)}>
            <BarChart data={data.by_department} layout="vertical" margin={{ left: 30, right: 12 }}>
              <XAxis type="number" tick={{ fontSize: 11 }} stroke="#999"/>
              <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={120} stroke="#999"/>
              <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid #ddd", fontSize: 12 }}/>
              <Bar dataKey="count" radius={0}>
                {data.by_department.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]}/>)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        {/* By level */}
        <Panel title="Headcount by org level">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.by_level}>
              <XAxis dataKey="level" tick={{ fontSize: 11 }} stroke="#999"/>
              <YAxis tick={{ fontSize: 11 }} stroke="#999"/>
              <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid #ddd", fontSize: 12 }}/>
              <Bar dataKey="count" fill="#002FA7" radius={0}/>
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        {/* Source */}
        <Panel title="Data source">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={data.by_source} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={90}>
                {data.by_source.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]}/>)}
              </Pie>
              <Legend formatter={(v) => <span className="text-xs">{v}</span>}/>
              <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid #ddd", fontSize: 12 }}/>
            </PieChart>
          </ResponsiveContainer>
        </Panel>

        {/* Risk */}
        <Panel title="Risk indicators">
          <ul className="space-y-2 text-xs">
            <li className="flex items-center justify-between border-b border-neutral-100 py-2">
              <span className="flex items-center gap-2">
                <AlertTriangle size={13} className={data.wide_spans.length > 0 ? "text-[#D83B01]" : "text-neutral-400"}/>
                Wide spans (&gt; 10 reports)
              </span>
              <span className="font-mono text-[11px]">{data.wide_spans.length}</span>
            </li>
            <li className="flex items-center justify-between border-b border-neutral-100 py-2">
              <span className="flex items-center gap-2">
                <Layers size={13} className={data.deep_employee_count > 0 ? "text-[#D83B01]" : "text-neutral-400"}/>
                Deep employees (level ≥ 5)
              </span>
              <span className="font-mono text-[11px]">{data.deep_employee_count}</span>
            </li>
            <li className="flex items-center justify-between border-b border-neutral-100 py-2">
              <span>Manager : IC ratio</span>
              <span className="font-mono text-[11px]">
                1 : {data.individual_contributors && data.managers ? (data.individual_contributors / data.managers).toFixed(1) : "—"}
              </span>
            </li>
          </ul>
        </Panel>

        {/* Top spans */}
        <Panel title="Top spans of control" className="lg:col-span-2">
          <ul>
            {data.top_spans.map((s, i) => (
              <li key={i} className="flex items-center justify-between border-b border-neutral-100 px-1 py-2 last:border-0" data-testid={`top-span-${i}`}>
                <div>
                  <p className="text-sm font-medium">{s.name}</p>
                  <p className="text-xs text-neutral-600">{s.designation || "—"}</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="h-1.5 w-32 bg-neutral-100">
                    <div className="h-full" style={{ width: `${(s.reports / data.top_spans[0].reports) * 100}%`, background: s.reports > 10 ? "#D83B01" : "#002FA7" }}/>
                  </div>
                  <span className="w-10 text-right font-mono text-xs">{s.reports}</span>
                </div>
              </li>
            ))}
          </ul>
        </Panel>
      </div>
    </div>
  );
}

function KPI({ icon: Icon, label, value, testId }) {
  return (
    <div className="bg-white px-5 py-6" data-testid={testId}>
      <Icon size={16} className="text-[#002FA7]"/>
      <p className="mt-3 text-3xl tracking-tight" style={{ fontFamily: '"Work Sans", sans-serif' }}>{value}</p>
      <p className="mt-1 text-[10px] uppercase tracking-[0.18em] text-neutral-500" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>{label}</p>
    </div>
  );
}

function Panel({ title, children, className = "" }) {
  return (
    <div className={`border border-neutral-200 bg-white ${className}`}>
      <div className="border-b border-neutral-200 px-5 py-3">
        <h3 className="text-base tracking-tight" style={{ fontFamily: '"Work Sans", sans-serif' }}>{title}</h3>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}
